import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { channelPairs } from '@/lib/channels';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGoBack } from '@/lib/goBack';
import { useAuth } from '@/lib/auth';
import { ChatTags, StatusChip } from '@/components/ChatRow';
import { ReassignSheet } from '@/components/ReassignSheet';
import { setPanel, usePanels } from '@/lib/panels';
import { dueOf, leafTasks, setInfoTab, useTedTasks } from '@/lib/tedTasks';
import { PanelLeftIcon, PanelRightIcon, TasksIcon } from '@/components/Icons';
import { LabelLine } from '@/components/Labels';
import { isMessage, previewOf, useChats, type LinkButton } from '@/data/store';
import { QUICK_REPLY_TEXT, approvedMessagesFor } from '@/data/sample';
import type { Chat, ChatMessage, TeamChat } from '@/data/types';
import { Avatar, TeamAvatar } from '@/components/Avatar';
import {
  BackIcon, BubbleIcon, CalendarIcon, CameraIcon, MailIcon, CloseIcon, CopyIcon, DocumentIcon, ForwardIcon,
  EditIcon, InfoIcon, MicIcon, NoteIcon, PlusIcon, SmileIcon, SparkIcon, PinIcon, ReplyIcon, SendIcon, StarIcon, TagIcon, TrashIcon,
  LinkIcon,
} from '@/components/Icons';
import { SheetOption, useOverlay } from '@/components/Overlay';
import { setLeoOnDuty, useLeoOnDuty } from '@/lib/duty';
import { QuickLinksSheet } from '@/components/QuickLinksSheet';
import { Thread } from '@/components/Thread';
import { EmailDraftSheet } from '@/components/EmailDraftSheet';
import { lastCustomerMessage } from '@/lib/suggest';
import { startRecording, type Recording } from '@/lib/voice';
import { UNDO_SECONDS, readUndoSend } from '@/lib/undoSend';
import { isPhone, useInfoPanel, useWide } from '@/lib/device';
import { shrinkPhoto } from '@/lib/shrinkPhoto';
import { MentionPicker } from '@/components/MentionPicker';
import { plainAnswer } from '@/lib/answer';
import { printPost } from '@/lib/printPost';

const SERVICE_SIGN_OFF = 'CleanMyBed - Customer Service';
const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
// The message box's emoji panel, for computers without an emoji keyboard (Gary, 15 Oct).
const EMOJIS = [
  '😊', '🙂', '😀', '😄', '😉', '😍', '🥰', '😎', '🤗', '🤔', '😅', '😂',
  '🙏', '👍', '👌', '👏', '🙌', '💪', '🤝', '👋', '✌️', '🤞', '✅', '☑️',
  '❤️', '💜', '💚', '⭐', '✨', '🎉', '🔥', '💯', '😴', '🛏️', '🧼', '🧽',
  '🏠', '📅', '⏰', '📍', '🚐', '📞', '💬', '📸', '🌿', '☀️', '😢', '😮',
];
const CANCEL_DISTANCE = 90;

/** A typed reply to a customer waiting UNDO_SECONDS before it goes (Gary, 16 Oct). */
interface PendingReply { chatId: string; text: string; replyTo: ChatMessage | null; quote: ChatMessage['replyTo']; sign: boolean; link: LinkButton | null }
/** Team messages may be edited or deleted for everyone this long after sending (Gary, 16 Oct). */
const TEAM_CHANGE_MS = 15 * 60_000;
const LOCK_DISTANCE = 70;

function authorOf(m: ChatMessage, chat: Chat): string {
  // In a team chat the sender, not the chat's name (mobile review, 19 Oct).
  if (m.from === 'in') return m.author ?? chat.name;
  if (m.from === 'auto') return 'Auto reply';
  if (m.from === 'note') return 'Team note';
  return 'You';
}

/**
 * The top line (Gary, 23 Oct): the owner's name, and who is on duty with one switch to flip. Nothing else.
 * Green for the person, violet for Leo; amber and locked when Leo is off for this customer.
 */
function ChatStrip({ chat, onDrc, onLeoBackOn, onOwner }: {
  chat: Chat;
  /** Head office: tap the owner's name to give the chat to someone else (Gary, 23 Oct). */
  onOwner?: () => void;
  onDrc: (action: 'return_to_ai' | 'ai_off') => void;
  /** Leo off for this customer: the locked switch offers to turn him back on. */
  onLeoBackOn?: () => void;
}) {
  const name = chat.takenOver ? 'You' : chat.owner?.split(' ')[0] ?? 'Head office';
  // Leo is on Facebook and Instagram chats too (Gary, 3 Nov 2026): the same switch as WhatsApp.
  const leoHere = chat.brand === 'cmb' && !chat.email;
  const leo = leoHere && (chat.aiState === 'ai' || (chat.aiState === 'human' && chat.onDuty === 'leo'));
  const flip = () => onDrc(leo ? 'ai_off' : 'return_to_ai');
  return (
    <div className={`chats-timer chats-timer--${leo ? 'drc' : 'person'}`}>
      <span className="chats-timer-text">Owner:{' '}
        {onOwner
          ? <button type="button" className="chats-owner-pick" onClick={onOwner}>{chat.takenOver ? 'You' : chat.owner?.split(' ')[0] ?? 'No owner yet'} ▾</button>
          : <b>{chat.takenOver ? 'You' : chat.owner?.split(' ')[0] ?? 'No owner yet'}</b>}
      </span>
      {/* Leo off for this customer (Gary, 23 Oct): no switch to contradict it; the person is on duty, and a tap offers
          to turn Leo back on. */}
      {leoHere && chat.leoOff && (
        <button type="button" className="chats-duty-flip" onClick={onLeoBackOn} disabled={!onLeoBackOn}>
          <span className="chats-duty-flip-label">On duty</span>{name}<span className="chats-duty-flip-label">· Leo is off for this customer</span>
        </button>
      )}
      {leoHere && !chat.leoOff && (
        <button type="button" className="chats-duty-flip" onClick={flip}
          aria-label={leo ? `Leo is on duty. Switch to ${name}` : `${name} on duty. Switch to Leo`}>
          <span className="chats-duty-flip-label">On duty</span>
          <span className={leo ? 'chats-duty-flip-off' : undefined}>{name}</span>
          <span className={`chats-switch${leo ? ' chats-switch--on' : ''}`} />
          <span className={leo ? undefined : 'chats-duty-flip-off'}>Leo</span>
        </button>
      )}
    </div>
  );
}

/** A Team chat in the shape of a customer chat, so it opens in this same screen (Gary, 15 Oct). */
function teamAsChat(t: TeamChat): Chat {
  return {
    id: t.id, brand: 'cmb', name: t.name, area: '',
    detail: t.kind === 'channel' ? 'Channel' : t.kind === 'group' ? `${t.members?.length ?? 0} members` : 'Team chat',
    colour: t.colour, state: 'mine',
    unread: t.unread ?? 0, time: t.time, windowOpen: true, quickReplies: [], thread: t.thread,
    archived: t.archived, isTeam: true, personId: t.personId, takenOver: true,
  };
}

export function ChatScreen({ team: teamMode = false }: { team?: boolean } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack();
  // ?m= a message to open at (search and starred messages on Info).
  const focusId = useSearchParams()[0].get('m') ?? undefined;
  const { access, profile } = useAuth();
  // Wide enough for the list and the chat side by side: computers and tablets, held either
  // way (Gary, 16 Oct). No phone is this wide, even turned sideways.
  const wide = useWide();
  const room = useInfoPanel() && wide;
  // Either side panel can be hidden for a full-width chat (Gary, 18 Oct).
  const panels = usePanels();
  const {
    live, sending, chats, team, typing, markRead, send, sendFile, sendTeam, takeOver, react, toggleStar, deleteForMe, drcAction, setLeoOff,
    markTeamRead, markTeamDone, sendTeamFile, teamReact, teamStar, teamHide, teamEdit, teamDelete, forwardMedia, closeChat, tellLeo,
  } = useChats();
  const { openSheet, closeSheet, toast } = useOverlay();
  // Replying while Leo is on duty for you (Barry's test, 19 Oct): one tap back to Available.
  const onDuty = useLeoOnDuty(live);
  const [askAvailable, setAskAvailable] = useState(false);
  const [text, setText] = useState('');
  // Head office replies sign as the team; this adds the sender's first name, remembered on this phone.
  const [signWithName, setSignWithName] = useState(() => {
    try { return localStorage.getItem('chats.signWithName') === '1'; } catch { return false; }
  });
  const myFirstName = (profile?.first_name ?? '').trim().split(/\s+/)[0] ?? '';
  function toggleSignWithName() {
    const next = !signWithName;
    setSignWithName(next);
    try { localStorage.setItem('chats.signWithName', next ? '1' : '0'); } catch { /* storage blocked: holds for this visit */ }
  }
  const [noteMode, setNoteMode] = useState(false);
  // Head office posts as Leo in a channel (Gary, 22 Oct 2026): one message at a time, like a team note.
  const [asLeo, setAsLeo] = useState(false);
  // Coaching groups (Gary, 1 Nov 2026): head office writes notes by default; posting to the operators is a second,
  // confirmed step, for one message at a time.
  const [postLive, setPostLive] = useState(false);
  // Tell Leo (Gary, 22 Oct 2026): head office's private instruction to Leo in a customer chat, one at a time.
  const [leoNote, setLeoNote] = useState(false);
  const [replyNow, setReplyNow] = useState(true);
  useEffect(() => { setAsLeo(false); setLeoNote(false); setPostLive(false); }, [id]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  // Editing one of your own Team messages (Gary, 16 Oct): its text is in the box until sent.
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  // A Quick link's page, sent as a button under the words (Gary, 16 Oct: no raw links).
  const [link, setLink] = useState<LinkButton | null>(null);
  // locked: recording hands-free (slid up on a phone, or clicked on a computer), with
  // Cancel, Stop to listen back, and Send, as WhatsApp (Gary, 16 Oct).
  const [recording, setRecording] = useState<{ since: number; x: number; y: number; locked: boolean } | null>(null);
  const [recordDx, setRecordDx] = useState(0);
  const [recordDy, setRecordDy] = useState(0);
  const [review, setReview] = useState<{ file: File; url: string } | null>(null);
  // A picked photo or PDF waits above the message box with its caption until Send, as
  // WhatsApp (Gary, 16 Oct); before, it went the moment it was picked.
  const [attach, setAttach] = useState<{ file: File; url: string | null } | null>(null);
  const attachUrl = useRef<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const [seconds, setSeconds] = useState(0);
  const input = useRef<HTMLTextAreaElement>(null);
  const recorder = useRef<Recording | null>(null);
  // The microphone starts a moment after the press (and the first time, after the permission
  // prompt). Each start has a number; a start that finishes after it was cancelled, or after
  // leaving the chat, is stopped at once, so the microphone is never left on (16 Oct).
  const startToken = useRef(0);
  const starting = useRef<Promise<Recording | null> | null>(null);
  const reviewUrl = useRef<string | null>(null);
  const photoPicker = useRef<HTMLInputElement>(null);
  const pdfPicker = useRef<HTMLInputElement>(null);

  const teamChat = teamMode ? team.find((t) => t.id === id) : undefined;
  const chat: Chat | undefined = teamMode ? (teamChat ? teamAsChat(teamChat) : undefined) : chats.find((c) => c.id === id);
  const headOffice = access.role === 'head_office';
  // Ted's weekly sales list, one tap from a group he coaches (Gary, 1 Nov 2026).
  const tedTasks = useTedTasks(live && teamChat?.kind === 'group' ? teamChat.id : '', null, teamChat?.thread.length ?? 0);
  // Names, not batches (Gary, 2 Nov 2026: a task is a batch of names).
  const tedNames = leafTasks(tedTasks.tasks);
  const openTasks = tedNames.filter((t) => t.status === 'open').length;
  // Unread count as the chat opened, for the "unread messages" line. Fixed for this visit.
  const [unreadAtOpen] = useState(() => chat?.unread ?? 0);

  useEffect(() => {
    // Opening the chat reads it. On live chats the owner's read also sends blue ticks to the customer.
    if (chat && (chat.unread || chat.markedUnread)) {
      if (chat.isTeam) markTeamRead(chat.id);
      else markRead(chat.id);
    }
  }, [chat?.id, chat?.unread, chat?.markedUnread, markRead]);

  useEffect(() => {
    if (!recording) return;
    const t = window.setInterval(() => setSeconds(Math.floor((Date.now() - recording.since) / 1000)), 250);
    return () => window.clearInterval(t);
  }, [recording]);

  // Leaving the chat or putting the app away stops a recording and frees a voice note
  // waiting to be listened back to, so the microphone is not left on (16 Oct).
  useEffect(() => {
    const stopMic = () => {
      startToken.current += 1;
      starting.current = null;
      recorder.current?.cancel();
      recorder.current = null;
    };
    const away = () => {
      if (document.visibilityState !== 'hidden' || (!recorder.current && !starting.current)) return;
      stopMic();
      setRecording(null);
    };
    document.addEventListener('visibilitychange', away);
    return () => {
      document.removeEventListener('visibilitychange', away);
      stopMic();
      if (reviewUrl.current) URL.revokeObjectURL(reviewUrl.current);
      if (attachUrl.current) URL.revokeObjectURL(attachUrl.current);
    };
  }, []);

  // The message box grows with what is typed, up to five lines, then scrolls.
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text]);

  // A reply to a customer waiting to go, with Undo (Gary, 16 Oct: WhatsApp cannot unsend).
  // It goes at once if the chat is left or the app is put away, so nothing is lost.
  const [pending, setPending] = useState<PendingReply | null>(null);
  const pendingRef = useRef<PendingReply | null>(null);
  // When the last message went: a second tap on Send lands on the microphone that replaces it (mobile review, 19 Oct).
  const lastSendAt = useRef(0);
  const pendingTimer = useRef<number | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;
  const flushPending = () => {
    const p = pendingRef.current;
    if (!p) return;
    pendingRef.current = null;
    if (pendingTimer.current !== null) window.clearTimeout(pendingTimer.current);
    pendingTimer.current = null;
    setPending(null);
    sendRef.current(p.chatId, { body: { kind: 'text', text: p.text }, replyTo: p.quote, signWithName: p.sign, link: p.link ?? undefined });
  };
  useEffect(() => {
    const away = () => { if (document.visibilityState === 'hidden') flushPending(); };
    document.addEventListener('visibilitychange', away);
    window.addEventListener('pagehide', flushPending);
    return () => {
      document.removeEventListener('visibilitychange', away);
      window.removeEventListener('pagehide', flushPending);
      flushPending();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flushPending reads refs only
  }, [chat?.id]);

  if (!chat) return teamMode && live ? <div className="boot"><div className="boot-spin" /></div> : <Navigate to={teamMode ? '/team' : '/'} replace />;
  const current = chat;

  const isTeam = !!current.isTeam;
  // No reply quotes on email, or in a channel only Sentinel posts in (mobile review, 19 Oct).
  const canReply = !current.email && !(isTeam && teamChat?.canPost === false);
  // WhatsApp and Email as tabs over one customer's conversation (Gary, 19 Oct): each tab is its own chat underneath.
  const pair = isTeam ? undefined : channelPairs(chats).get(current.id);
  // Head office on an operator's chat: they can reply, add a note and ask Dr C without taking
  // it over (Gary, 16 Oct). The chat stays with its owner, who sees who sent each message.
  const othersChat = !isTeam && headOffice && !current.inbox && !current.takenOver;
  // As on Info: saved on the customer or lead, so the chat needs one; its owner or head office (Gary, 20 Oct).
  const canLeoOff = !isTeam && current.brand === 'cmb' && !current.email && (!live || ((!!current.customerId || !!current.leadId) && (headOffice || current.ownerId === profile?.id)));
  // Head office on someone else's chat is asked on send: take over, team note or Tell Leo (Gary, 23 Oct: a head office
  // "Excellent" in Barry's chat read to the customer as Barry; a standing bar was noise).
  const coachNote = isTeam && access.superAdmin === true && !!teamChat?.coaching && !teamChat.coachTest && !postLive;
  const noting = noteMode || coachNote;
  // A business (Peace of Sleep, or a company) is named in full; a person by first name.
  const firstName = current.brand === 'pos' ? current.name : current.name.split(' ')[0] ?? current.name;
  const ownerFirst = current.owner?.split(' ')[0] || 'The operator';
  const canWrite = current.windowOpen;
  // An older, closed email (opened from Info) is a record: its customer's current email chat, if there is one, is
  // where replies go (mobile review, 19 Oct).
  const oldEmail = !isTeam && !!current.email && (!!current.closed || !!current.archived);
  const currentEmail = oldEmail
    ? chats.find((c) => c.id !== current.id && !!c.email && !c.closed && !c.archived
      && ((!!current.leadId && c.leadId === current.leadId) || (!!current.customerId && c.customerId === current.customerId)))
    : undefined;

  function quoteOf(m: ChatMessage): ChatMessage['replyTo'] {
    return { id: m.id, author: authorOf(m, current), preview: previewOf(m.body) };
  }

  function startReply(m: ChatMessage) {
    // An email carries no quote, so an email chat offers none (mobile review, 19 Oct).
    if (current.email) return;
    if (!canWrite) {
      toast('Only approved messages until the customer replies');
      return;
    }
    setReplyTo(m);
    // Straight away, inside the tap: iPhone opens the keyboard only then (16 Oct).
    input.current?.focus({ preventScroll: true });
  }

  /** Head office pressed send on someone else's chat: one question, then it goes (Gary, 23 Oct). */
  function askHowToSend(value: string) {
    const done = () => { closeSheet(); setText(''); setReplyTo(null); setLink(null); };
    const owner = current.owner ? ownerFirst : 'the team';
    openSheet(
      <>
        <SheetOption icon={<SendIcon />} title={`Take over and send to ${firstName}`} sub={current.owner ? `The chat moves from ${ownerFirst} to you` : 'The chat becomes yours'}
          onClick={() => { takeOver(current.id, SERVICE_SIGN_OFF); send(current.id, { body: { kind: 'text', text: value }, replyTo: replyTo ? quoteOf(replyTo) : undefined, signWithName: !!current.signOff && signWithName, link: link ?? undefined }); done(); }} />
        <SheetOption icon={<NoteIcon />} title={`Team note to ${owner}`} sub={`${firstName} won't see it`}
          onClick={() => { send(current.id, { body: { kind: 'text', text: value }, note: true }); done(); }} />
        {current.brand === 'cmb' && !current.email && (
          <SheetOption icon={<SparkIcon />} title="Tell Leo" sub="Only Leo sees it: he follows it in this chat"
            onClick={() => { tellLeo(current.id, value, current.aiState === 'ai'); done(); }} />
        )}
      </>,
    );
  }

  /** The locked switch (Leo off for this customer): one tap offers to turn him back on (Gary, 23 Oct). */
  function leoBackOn() {
    openSheet(
      <SheetOption icon={<SparkIcon />} title="Leo back on for this customer" sub="Leo helps them again, in this chat and later ones" onClick={() => { closeSheet(); setLeoOff(current.id, false); }} />,
    );
  }

  function sendText(e?: FormEvent) {
    e?.preventDefault();
    const value = text.trim();
    if (attach && (!noting || coachNote) && !sending) {
      sendAttach(value);
      return;
    }
    if (!value || sending) return;
    if (leoNote) {
      tellLeo(current.id, value, replyNow && current.aiState === 'ai');
      setLeoNote(false);
      setText('');
      return;
    }
    if (othersChat && !noteMode && !editing) {
      askHowToSend(value);
      return;
    }
    if (editing) {
      teamEdit(current.id, editing.id, value);
      setEditing(null);
    } else if (isTeam) void sendTeam(current.id, value, false, replyTo?.id, asLeo, postLive);
    else if (!noting && readUndoSend()) {
      // One waiting at a time: an earlier one goes now.
      flushPending();
      const p: PendingReply = { chatId: current.id, text: value, replyTo, quote: replyTo ? quoteOf(replyTo) : undefined, sign: !!current.signOff && signWithName, link };
      pendingRef.current = p;
      setPending(p);
      pendingTimer.current = window.setTimeout(flushPending, UNDO_SECONDS * 1000);
    } else send(current.id, { body: { kind: 'text', text: value }, note: noting, replyTo: replyTo ? quoteOf(replyTo) : undefined, signWithName: !!current.signOff && signWithName, link: noting ? undefined : link ?? undefined });
    if (!isTeam && !noting && !editing && onDuty) setAskAvailable(true);
    lastSendAt.current = Date.now();
    setText('');
    setReplyTo(null);
    setLink(null);
    // A team note is one at a time: the box goes back to a normal reply (Gary, 15 Oct). So is a post as Leo.
    if (noteMode) setNoteMode(false);
    if (asLeo) setAsLeo(false);
    if (postLive) setPostLive(false);
    // The box stays ready for the next message, as WhatsApp (Gary, 16 Oct; this replaces the
    // keyboard closing after each send, 15 Oct). Send keeps the box focused (see the button).
    input.current?.focus({ preventScroll: true });
  }

  // An emoji goes where the cursor is, and the panel stays open for more.
  function insertEmoji(e: string) {
    const el = input.current;
    const at = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    setText(text.slice(0, at) + e + text.slice(end));
    window.setTimeout(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(at + e.length, at + e.length);
    }, 0);
  }

  // On a computer Enter sends and Shift+Enter starts a new line (Gary, 15 Oct). On a phone
  // Enter is a new line and the arrow sends, as WhatsApp (Gary, 16 Oct), so an address can
  // go on separate lines. Not while a keyboard is composing a word.
  function composerKey(e: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if ((isPhone && !wide) || e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    sendText();
  }

  // Hold the mic to record, slide left to cancel, let go to send: WhatsApp's voice note.
  // Live chats record for real (lib/voice.ts); sample chats only pretend.
  // Phone: hold to record and let go to send; slide left to cancel, slide up to lock.
  // Computer (a mouse): one click starts a locked recording.
  function micDown(e: ReactPointerEvent<HTMLButtonElement>) {
    if (text.trim()) return;
    // A double tap on Send: the second tap starts no recording and no hint (mobile review, 19 Oct).
    if (Date.now() - lastSendAt.current < 800) return;
    const mouse = e.pointerType === 'mouse';
    if (!mouse) e.currentTarget.setPointerCapture(e.pointerId);
    setRecording({ since: Date.now(), x: e.clientX, y: e.clientY, locked: mouse });
    setRecordDx(0);
    setRecordDy(0);
    setSeconds(0);
    if (live) {
      recorder.current = null;
      const token = ++startToken.current;
      starting.current = startRecording().then((r) => {
        if (token !== startToken.current) { r.cancel(); return null; }
        recorder.current = r;
        return r;
      }).catch(() => {
        if (token === startToken.current) {
          setRecording(null);
          toast('Allow the microphone for CMB Chats to send voice notes');
        }
        return null;
      });
    }
  }
  function micMove(e: ReactPointerEvent<HTMLButtonElement>) {
    if (!recording || recording.locked) return;
    const dy = Math.min(0, e.clientY - recording.y);
    if (dy <= -LOCK_DISTANCE) {
      setRecording({ ...recording, locked: true });
      setRecordDx(0);
      setRecordDy(0);
      return;
    }
    setRecordDx(Math.min(0, e.clientX - recording.x));
    setRecordDy(dy);
  }
  function micUp() {
    if (!recording || recording.locked) return;
    const held = Date.now() - recording.since;
    const cancelled = recordDx <= -CANCEL_DISTANCE;
    if (cancelled || held < 600) {
      cancelRecording();
      if (!cancelled) toast('Hold to record, let go to send');
      return;
    }
    void finishRecording(false);
  }
  function cancelRecording() {
    setRecording(null);
    setRecordDx(0);
    setRecordDy(0);
    startToken.current += 1;
    starting.current = null;
    recorder.current?.cancel();
    recorder.current = null;
  }
  /** Stops the recording, then sends it, or keeps it to listen back first. */
  async function finishRecording(listenFirst: boolean) {
    const started = recording?.since ?? Date.now();
    const pendingStart = starting.current;
    starting.current = null;
    setRecording(null);
    setRecordDx(0);
    setRecordDy(0);
    if (!live) {
      const secs = Math.max(1, Math.min(59, Math.round((Date.now() - started) / 1000)));
      send(current.id, { body: { kind: 'voice', duration: `0:${String(secs).padStart(2, '0')}` }, replyTo: replyTo ? quoteOf(replyTo) : undefined });
      setReplyTo(null);
      return;
    }
    // A short hold can end before the microphone has started: wait for it, then stop it.
    const rec = recorder.current ?? (pendingStart ? await pendingStart : null);
    recorder.current = null;
    startToken.current += 1;
    if (!rec) { toast('The microphone did not start. Try again.'); return; }
    const file = await rec.stop();
    if (!file) { toast('Nothing was recorded. Try again.'); return; }
    if (listenFirst) {
      const url = URL.createObjectURL(file);
      reviewUrl.current = url;
      setReview({ file, url });
    }
    else sendVoice(file);
  }
  function sendVoice(file: File) {
    if (isTeam) { sendTeamFile(current.id, file, undefined, postLive); setPostLive(false); }
    else sendFile(current.id, file);
  }
  function closeReview(sendIt: boolean) {
    if (!review) return;
    URL.revokeObjectURL(review.url);
    reviewUrl.current = null;
    if (sendIt) sendVoice(review.file);
    setReview(null);
  }

  function sendQuick(label: string) {
    const q = QUICK_REPLY_TEXT[label];
    if (!q) return;
    send(current.id, {
      body: q.link ? { kind: 'link', title: q.link.title, url: q.link.url, text: q.text } : { kind: 'text', text: q.text },
      button: q.button,
    });
  }

  function applyReplyLine(line: string, page?: LinkButton) {
    // Focus first, inside the tap, so the keyboard opens on iPhone (16 Oct).
    input.current?.focus({ preventScroll: true });
    closeSheet();
    setNoteMode(false);
    // Added after what is already typed, never over it (mobile review, 19 Oct).
    setText((prev) => (prev.trim() ? `${prev.trimEnd()}\n${line}` : line));
    setLink((prev) => page ?? prev);
  }

  function forwardSheet(m: ChatMessage) {
    // A team note never goes to a customer: team chats only (mobile review, 19 Oct).
    // AI team posts are internal too (Gary, 1 Nov): team chats only, sent as plain text with the writer's name.
    const note = m.from === 'note' || !!m.aiTeam;
    const customers = note ? [] : chats.filter((c) => c.id !== current.id && !c.archived && (headOffice || !c.inbox));
    // Photos, PDFs and voice notes go as the file itself (Sentinel copies it); text as text.
    const isFile = live && m.body.kind !== 'text';
    const source = isTeam ? 'team' : 'chat';
    openSheet(
      <>
        <h2 className="chats-sheet-title">Forward to</h2>
        {!note && <p className="chats-sheet-hint">Customers only receive it while their chat is open (they wrote in the last 24 hours).</p>}
        {/* Only chats you can post in, and "Forwarded" once it has gone (mobile review, 19 Oct: Platform Health). */}
        {team.filter((t) => t.id !== current.id && t.canPost !== false).map((t) => (
          <SheetOption key={t.id} icon={<Avatar name={t.name} colour={t.colour} small />} title={t.name} sub="Team chat" onClick={() => {
            closeSheet();
            void (isFile ? forwardMedia(source, m.id, 'team', t.id) : sendTeam(t.id, m.aiTeam && m.body.kind === 'text' ? `${m.author ?? 'AI team'}: ${plainAnswer(m.body.text)}` : previewOf(m.body), true)).then((ok) => { if (ok) toast(`Forwarded to ${t.name}`); });
          }} />
        ))}
        {customers.map((c) => (
          <SheetOption
            key={c.id}
            icon={<Avatar name={c.name} colour={c.colour} small />}
            title={c.name}
            sub={c.windowOpen ? c.area : `${c.area} · approved messages only`}
            onClick={() => {
              if (!c.windowOpen) { toast(`${c.name.split(' ')[0]} has not written in 24 hours. Use an approved message.`); return; }
              closeSheet();
              if (isFile) { void forwardMedia(source, m.id, 'chat', c.id).then((ok) => { if (ok) toast(`Forwarded to ${c.name}`); }); return; }
              send(c.id, { body: m.body, forwarded: true });
              toast(`Forwarded to ${c.name}`);
            }}
          />
        ))}
      </>,
    );
  }

  function infoSheet(m: ChatMessage) {
    openSheet(
      <>
        <h2 className="chats-sheet-title">Message info</h2>
        <p className="chats-sheet-hint">{previewOf(m.body)}</p>
        <dl className="chats-info-list">
          <div><dt>Sent</dt><dd>{m.at}</dd></div>
          <div><dt>Delivered</dt><dd>{m.deliveredAt ?? (m.delivery === 'delivered' || m.delivery === 'read' ? m.at : 'Not yet')}</dd></div>
          <div><dt>Read</dt><dd>{m.readAt ?? (m.delivery === 'read' ? m.at : 'Not yet')}</dd></div>
        </dl>
      </>,
    );
  }

  function deleteSheet(m: ChatMessage) {
    openSheet(
      <>
        <h2 className="chats-sheet-title">Delete message?</h2>
        <p className="chats-sheet-hint">{isTeam ? 'It disappears from this chat for you only. The others in this chat still have it.' : 'It disappears from this chat for you only. The customer still has it, and Sentinel keeps the record.'}</p>
        <div className="chats-pair">
          <button type="button" className="chats-btn chats-btn--ghost" onClick={closeSheet}>Cancel</button>
          <button type="button" className="chats-btn chats-btn--danger" onClick={() => { closeSheet(); if (isTeam) teamHide(current.id, m.id); else deleteForMe(current.id, m.id); toast('Message deleted for you'); }}>Delete for me</button>
        </div>
      </>,
    );
  }

  function deleteForEveryoneSheet(m: ChatMessage) {
    openSheet(
      <>
        <h2 className="chats-sheet-title">Delete for everyone?</h2>
        <p className="chats-sheet-hint">Everyone in this chat sees "This message was deleted" in its place.</p>
        <div className="chats-pair">
          <button type="button" className="chats-btn chats-btn--ghost" onClick={closeSheet}>Cancel</button>
          <button type="button" className="chats-btn chats-btn--danger" onClick={() => { closeSheet(); teamDelete(current.id, m.id); toast('Message deleted for everyone'); }}>Delete</button>
        </div>
      </>,
    );
  }

  function messageMenu(m: ChatMessage) {
    // The keyboard goes down first, so the menu has the screen, as WhatsApp (16 Oct).
    input.current?.blur();
    const isText = m.body.kind === 'text' || m.body.kind === 'link';
    const ours = m.from === 'out' || m.from === 'auto';
    // A deleted Team message keeps only Delete for me.
    if (m.deleted) {
      openSheet(<SheetOption icon={<TrashIcon />} title="Delete for me" sub="Hides it for you" danger onClick={() => deleteSheet(m)} />);
      return;
    }
    // Your own Team message, within 15 minutes of sending (Gary, 16 Oct). Customer chats
    // cannot: WhatsApp has no edit or unsend for a business.
    const changeable = isTeam && m.from === 'out' && !!m.sentAt && Date.now() - Date.parse(m.sentAt) < TEAM_CHANGE_MS;
    // WhatsApp lets a business react only to the customer's messages.
    // Not on an email chat: reactions are WhatsApp's (mobile review, 19 Oct).
    const reactions = !current.email && m.from !== 'note' && (m.from === 'in' || !live || isTeam) && (
          <div className="chats-react-bar" role="group" aria-label="React">
            {REACTIONS.map((r) => (
              <button
                key={r}
                type="button"
                className={m.reaction === r ? 'active' : undefined}
                aria-label={`React ${r}`}
                onClick={() => { closeSheet(); (isTeam ? teamReact : react)(current.id, m.id, m.reaction === r ? null : r); }}
              >
                {r}
              </button>
            ))}
          </div>
        );
    // In place, as WhatsApp: the message lifted, reactions above, options below (16 Oct).
    const place = { anchor: document.querySelector<HTMLElement>(`[data-msg="${CSS.escape(m.id)}"] .chats-msg-wrap`), top: reactions || undefined, outgoing: m.from !== 'in' };
    openSheet(
      <>
        {!place.anchor && reactions}
        <p className="chats-sheet-hint chats-menu-preview">{previewOf(m.body)}</p>
        {canReply && <SheetOption icon={<ReplyIcon />} title="Reply" sub="Quote this message in your reply" onClick={() => { closeSheet(); startReply(m); }} />}
        {isText && (
          <SheetOption
            icon={<CopyIcon />}
            title="Copy"
            sub="Copy the text"
            onClick={() => {
              closeSheet();
              const t = m.body.kind === 'text' ? (m.aiTeam ? plainAnswer(m.body.text) : m.body.text) : m.body.kind === 'link' ? `${m.body.text} ${m.body.url}` : '';
              void navigator.clipboard?.writeText(t).then(() => toast('Copied'), () => toast('Copy is not allowed here'));
            }}
          />
        )}
        {m.aiTeam && m.body.kind === 'text' && (
          <SheetOption
            icon={<DocumentIcon />}
            title="Print or save as PDF"
            sub="A clean page of this post"
            onClick={() => {
              closeSheet();
              const text = m.body.kind === 'text' ? m.body.text : '';
              if (!printPost(`${m.author ?? 'AI team'}, ${current.name}`, `${m.sentAt ? new Date(m.sentAt).toLocaleString('en-GB') : m.at}`, text)) toast('Allow pop-ups to print');
            }}
          />
        )}
        <SheetOption icon={<ForwardIcon />} title="Forward" sub={m.from === 'note' || m.aiTeam ? 'To a team chat' : 'To a team chat or another customer'} onClick={() => forwardSheet(m)} />
        <SheetOption icon={<StarIcon filled={m.starred} />} title={m.starred ? 'Unstar' : 'Star'} sub={isTeam ? "Find it later under this chat's Info" : "Find it later under the contact's starred messages"} onClick={() => { closeSheet(); (isTeam ? teamStar : toggleStar)(current.id, m.id); }} />
        {ours && <SheetOption icon={<InfoIcon />} title="Info" sub="When it was delivered and read" onClick={() => infoSheet(m)} />}
        {changeable && m.body.kind === 'text' && (
          <SheetOption
            icon={<EditIcon />}
            title="Edit"
            sub="Change the text; everyone sees it was edited"
            onClick={() => {
              closeSheet();
              setReplyTo(null);
              setEditing(m);
              setText(m.body.kind === 'text' ? m.body.text : '');
              input.current?.focus({ preventScroll: true });
            }}
          />
        )}
        {changeable && <SheetOption icon={<TrashIcon />} title="Delete for everyone" sub="Within 15 minutes of sending" danger onClick={() => deleteForEveryoneSheet(m)} />}
        <SheetOption icon={<TrashIcon />} title="Delete for me" sub={isTeam ? 'Hides it for you; the others still have it' : 'Hides it for you; the customer still has it'} danger onClick={() => deleteSheet(m)} />
      </>,
      place,
    );
  }

  // A photo or PDF picked on the phone waits above the box for a caption and Send. Photos
  // are made smaller first, as WhatsApp. Size limits are WhatsApp's for photos and ours for PDFs.
  async function pickFile(e: { target: HTMLInputElement }) {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    const pdf = picked.type === 'application/pdf';
    if (!pdf && !picked.type.startsWith('image/')) { toast('Send a photo or a PDF'); return; }
    const file = pdf ? picked : await shrinkPhoto(picked);
    if (!mounted.current) return;
    if (file.size > (pdf ? 16 : 5) * 1024 * 1024) { toast(pdf ? 'PDFs can be up to 16 MB' : 'This photo is too large to send'); return; }
    clearAttach();
    // A photo goes on its own: a reply, edit or page button in the box is put away.
    setReplyTo(null);
    setEditing(null);
    setLink(null);
    const url = pdf ? null : URL.createObjectURL(file);
    attachUrl.current = url;
    setNoteMode(false);
    setAttach({ file, url });
  }
  function clearAttach() {
    if (attachUrl.current) URL.revokeObjectURL(attachUrl.current);
    attachUrl.current = null;
    setAttach(null);
  }
  function sendAttach(caption: string) {
    if (!attach) return;
    if (isTeam) { sendTeamFile(current.id, attach.file, caption || undefined, postLive); setPostLive(false); }
    else sendFile(current.id, attach.file, caption || undefined);
    clearAttach();
    setText('');
    setReplyTo(null);
  }

  // The + menu (Gary, 15 Oct): everything added to a chat in one place, as WhatsApp on iPhone.
  /** Who reacted to a group or channel message, by emoji (Gary, 22 Oct 2026: a read receipt and a poll). */
  function whoReacted(m: ChatMessage) {
    const groups = m.reactionsAll ?? [];
    openSheet(
      <>
        {groups.map((g) => (
          <SheetOption key={g.emoji} icon={<span aria-hidden="true">{g.emoji}</span>} title={`${g.names.length} ${g.names.length === 1 ? 'person' : 'people'}`} sub={g.names.join(', ')} onClick={closeSheet} />
        ))}
      </>,
    );
  }

  function plusSheet() {
    input.current?.blur();
    openSheet(
      <>
        {/* Email chats take words only for now (Gary, 19 Oct: Leo on email, phase 1). */}
        {/* Facebook and Instagram take photos and documents as WhatsApp does (Gary, 3 Nov 2026). */}
        {!current.email && !othersChat && <>
        <SheetOption icon={<CameraIcon />} title="Photo" sub="From your camera or gallery" onClick={() => { closeSheet(); photoPicker.current?.click(); }} />
        <SheetOption icon={<DocumentIcon />} title="Document" sub="A PDF, such as a quote or receipt" onClick={() => { closeSheet(); pdfPicker.current?.click(); }} />
        </>}
        {/* Head office posts in a channel as Leo (Gary, 22 Oct 2026): everyone is alerted, as with any post. */}
        {isTeam && teamChat?.kind === 'channel' && !teamChat.system && headOffice && (
          <SheetOption icon={<SparkIcon />} title="Post as Leo" sub="Your next message goes out from Leo" onClick={() => { closeSheet(); setAsLeo(true); input.current?.focus({ preventScroll: true }); }} />
        )}
        {/* A colleague chat is already private: no team notes, customer questions or Dr C there. */}
        {!isTeam && <>
        <SheetOption icon={<NoteIcon />} title="Team note" sub="Only the team sees it, never the customer" onClick={() => { input.current?.focus({ preventScroll: true }); closeSheet(); clearAttach(); setNoteMode(true); }} />
        {/* Head office nudges Leo without stepping in (Gary, 22 Oct 2026); WhatsApp, Facebook and Instagram on CleanMyBed. */}
        {headOffice && !current.email && current.brand !== 'pos' && (
          <SheetOption icon={<SparkIcon />} title="Tell Leo" sub="Only Leo sees it: he follows it in this chat" onClick={() => { input.current?.focus({ preventScroll: true }); closeSheet(); clearAttach(); setNoteMode(false); setLeoNote(true); setReplyNow(current.aiState === 'ai'); }} />
        )}
        {/* Sample wording only: never sent to a real customer (live chats). */}
        {!live && <SheetOption icon={<TagIcon />} title="Price list" sub="Prices for this customer's area" onClick={() => { closeSheet(); sendQuick('Send price list'); }} />}
        {!live && <SheetOption icon={<CalendarIcon />} title="Available times" sub="Your open slots this week" onClick={() => { closeSheet(); send(current.id, { body: { kind: 'text', text: 'My open times this week: Thu 14:00, Fri 09:00, Sat 08:00 or 10:30.' } }); }} />}
        {/* Puts the wording in the message box to check and send; never sends on its own (Gary, 15 Oct). */}
        <SheetOption icon={<PinIcon />} title="Ask for address" sub="Puts the question in your message box" onClick={() => applyReplyLine(current.email ? 'Please reply with your full street address.' : 'Please share your location pin or type your street address.')} />
        {/* Links go only while WhatsApp's 24-hour window is open (Gary, 15 Oct). */}
        <SheetOption
          icon={<LinkIcon />}
          title="Quick links"
          sub={current.windowOpen ? 'Operator pages, Google reviews, pricing and more' : 'The 24 hours are up: only approved messages can go until they reply'}
          onClick={() => { if (current.windowOpen) openSheet(<QuickLinksSheet brand={current.brand} territoryId={current.territoryId} onUse={applyReplyLine} />); }}
        />
        <SheetOption
          icon={<SparkIcon />}
          title="Suggest a reply"
          sub="Leo drafts an answer for you to edit"
          // Customer Leo drafts it, with prices, areas and the Science page: an email on an email chat (Gary, 19 Oct).
          onClick={() => openSheet(<EmailDraftSheet chat={current} onUse={applyReplyLine} onClose={closeSheet} />)}
        />
        </>}
      </>,
    );
  }

  function approvedSheet() {
    // Live chats use the same four messages Meta approved as New chat (Gary, 15 Oct).
    if (live) { navigate(`/new?chat=${current.id}`); return; }
    openSheet(
      <>
        <h2 className="chats-sheet-title">Approved messages</h2>
        <p className="chats-sheet-hint">Meta approves these in advance. Use one to restart the chat. When the customer replies, you can type freely again.</p>
        {approvedMessagesFor(current).map((m) => (
          <button key={m.id} type="button" className="chats-tpl" onClick={() => { closeSheet(); send(current.id, { body: { kind: 'text', text: m.text }, approved: true }); }}>
            <b>{m.name}</b>
            <p>{m.text}</p>
          </button>
        ))}
      </>,
    );
  }

  let bottom;
  if (current.emailLead) {
    // A website enquiry with no mobile (Gary, 17 Oct): nothing can go by WhatsApp.
    bottom = (
      <div className="chats-closed">
        <p>Email lead: {firstName} gave no mobile, so WhatsApp cannot be used. Call them from the card above. If they reply to our emails, it lands here and you can answer. Close the chat when done.</p>
        <button type="button" className="chats-btn" onClick={() => closeChat(current.id, 'closed')}>Close chat</button>
      </div>
    );
  } else if (currentEmail) {
    bottom = (
      <div className="chats-closed">
        <p>This is an older, closed email. Reply in {firstName}'s current conversation.</p>
        <button type="button" className="chats-btn" onClick={() => navigate(`/chat/${currentEmail.id}`, { replace: true })}>Open the current conversation</button>
      </div>
    );
  } else if (!current.windowOpen && current.dm) {
    // Meta allows no approved messages on Facebook or Instagram: once 7 days pass, we wait for them (Gary, 3 Nov 2026).
    bottom = (
      <div className="chats-closed">
        <p>{firstName} last wrote more than 7 days ago. {current.dm === 'facebook' ? 'Facebook' : 'Instagram'} only lets us reply once they write again.</p>
        <button type="button" className="chats-btn" onClick={() => closeChat(current.id, 'closed')}>Close chat</button>
      </div>
    );
  } else if (!current.windowOpen) {
    bottom = (
      <div className="chats-closed">
        <p>{current.thread.some((i) => isMessage(i) && i.from === 'in')
          ? `${firstName} last wrote more than 24 hours ago. WhatsApp only lets you send an approved message until they reply. Once they do, you can chat normally again.`
          : `${firstName} has not written on WhatsApp yet. Only an approved message can go until they reply.`}</p>
        {othersChat
          ? <button type="button" className="chats-btn" onClick={() => takeOver(current.id, SERVICE_SIGN_OFF)}>Take over to message {firstName}</button>
          : <button type="button" className="chats-btn" onClick={approvedSheet}>Choose an approved message</button>}
      </div>
    );
  } else if (isTeam && teamChat?.canPost === false) {
    // A channel is one way (Gary, 15 Oct): head office posts, everyone reads, reacts or mutes.
    bottom = (
      <div className="chats-closed">
        <p>{teamChat.system
          ? 'Only Sentinel posts here: platform alerts for head office. Tap Done once handled and it clears for everyone; mute it from Info.'
          : 'Only head office posts in this channel. Hold a message to react; mute it from Info.'}</p>
      </div>
    );
  } else {
    const cancelling = recordDx <= -CANCEL_DISTANCE;
    const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
    bottom = review ? (
      <div className="chats-composer">
        <button type="button" className="chats-plus" aria-label="Delete voice note" onClick={() => closeReview(false)}><TrashIcon /></button>
        <audio className="chats-audio chats-rec-review" src={review.url} controls autoPlay={false} />
        <button type="button" className="chats-send" aria-label="Send voice note" disabled={sending} onClick={() => closeReview(true)}><SendIcon /></button>
      </div>
    ) : recording?.locked ? (
      <div className="chats-composer">
        <button type="button" className="chats-plus" aria-label="Delete voice note" onClick={cancelRecording}><TrashIcon /></button>
        <div className="chats-rec">
          <span className="chats-rec-dot" />
          <span>{clock}</span>
          {live && <button type="button" className="chats-rec-stop" onClick={() => void finishRecording(true)}>Stop to listen</button>}
        </div>
        <button type="button" className="chats-send" aria-label="Send voice note" onClick={() => void finishRecording(false)}><SendIcon /></button>
      </div>
    ) : (
      <>
        {current.quickReplies.length > 0 && !noting && !recording && !replyTo && !attach && (
          <div className="chats-quick">
            {current.quickReplies.map((q) => <button key={q} type="button" onClick={() => sendQuick(q)}>{q}</button>)}
          </div>
        )}
        {!noting && !recording && current.signOff && (
          <div className="chats-signing">
            <span>{signWithName && myFirstName ? <>Replies end with <b>{myFirstName}</b></> : 'Replies have no sign-off'}</span>
            {myFirstName && (
              <button type="button" className="chats-signing-toggle" aria-pressed={signWithName} onClick={toggleSignWithName}>
                {signWithName ? 'Remove my name' : 'Add my name'}
              </button>
            )}
          </div>
        )}
        {!noting && !recording && current.brand === 'pos' && !current.signOff && (
          <div className="chats-signing"><span>Replying from the <b>Peace of Sleep</b> number</span></div>
        )}
        {pending && (
          <div className="chats-replybar chats-undo-bar" role="status">
            <div className="chats-msg-quote">
              <b>Sending...</b>
              <span>{pending.text}</span>
              <i key={pending.text} className="chats-undo-progress" />
            </div>
            <button
              type="button"
              className="chats-undo-btn"
              onClick={() => {
                const p = pendingRef.current;
                if (!p) return;
                pendingRef.current = null;
                if (pendingTimer.current !== null) window.clearTimeout(pendingTimer.current);
                pendingTimer.current = null;
                setPending(null);
                // Back in front of anything typed since, never over it (mobile review, 19 Oct).
                setText((cur) => (cur.trim() ? `${p.text}\n${cur}` : p.text));
                setReplyTo((cur) => cur ?? p.replyTo);
                setLink((cur) => cur ?? p.link);
                input.current?.focus({ preventScroll: true });
              }}
            >
              Undo
            </button>
          </div>
        )}
        {link && !noting && (
          <div className="chats-replybar">
            <div className="chats-msg-quote">
              <b>{`Button: ${link.label}`}</b>
              <span>{link.url.replace('https://', '')}</span>
            </div>
            <button type="button" className="chats-icon-btn chats-icon-btn--field" aria-label="Remove the button" onClick={() => setLink(null)}><CloseIcon /></button>
          </div>
        )}
        {editing && (
          <div className="chats-replybar">
            <div className="chats-msg-quote">
              <b>Editing</b>
              <span>{previewOf(editing.body)}</span>
            </div>
            <button type="button" className="chats-icon-btn chats-icon-btn--field" aria-label="Cancel edit" onClick={() => { setEditing(null); setText(''); }}><CloseIcon /></button>
          </div>
        )}
        {replyTo && (
          <div className="chats-replybar">
            <div className="chats-msg-quote">
              <b>{authorOf(replyTo, current)}</b>
              <span>{previewOf(replyTo.body)}</span>
            </div>
            <button type="button" className="chats-icon-btn chats-icon-btn--field" aria-label="Cancel reply" onClick={() => setReplyTo(null)}><CloseIcon /></button>
          </div>
        )}
        {attach && (
          <div className="chats-replybar chats-attach-bar">
            {attach.url
              ? <img className="chats-attach-thumb" src={attach.url} alt="Photo to send" />
              : <span className="chats-attach-doc"><DocumentIcon /></span>}
            <div className="chats-msg-quote">
              <b>{attach.url ? 'Photo' : 'Document'}</b>
              <span>{attach.url ? 'Add a caption, or tap send' : attach.file.name}</span>
            </div>
            <button type="button" className="chats-icon-btn chats-icon-btn--field" aria-label="Remove" onClick={clearAttach}><CloseIcon /></button>
          </div>
        )}
        {emojiOpen && !recording && (
          <div className="chats-emoji-panel" role="group" aria-label="Emojis">
            {EMOJIS.map((e) => <button key={e} type="button" onClick={() => insertEmoji(e)}>{e}</button>)}
          </div>
        )}
        {leoNote && !recording && (
          <div className="chats-note-strip">
            <span>Tell Leo. {firstName} won't see this</span>
            {current.aiState === 'ai' && (
              <button type="button" aria-pressed={replyNow} onClick={() => setReplyNow((r) => !r)}>{replyNow ? 'Reply now: on' : 'Reply now: off'}</button>
            )}
            <button type="button" onClick={() => { setLeoNote(false); input.current?.focus(); }}>Cancel</button>
          </div>
        )}
        {asLeo && !recording && (
          <div className="chats-note-strip">
            <span>Posting as Leo</span>
            <button type="button" onClick={() => { setAsLeo(false); input.current?.focus(); }}>Post as you</button>
          </div>
        )}
        {coachNote && !noteMode && !recording && (
          <div className="chats-note-strip">
            <span>Note. Only head office and Ted see this</span>
            <button type="button" onClick={() => openSheet(
              <>
                <h2 className="chats-sheet-title">Post to the group?</h2>
                <p className="chats-sheet-hint">{`Your next message goes to everyone in ${current.name}, not as a note.`}</p>
                <div className="chats-pair">
                  <button type="button" className="chats-btn chats-btn--ghost" onClick={closeSheet}>Cancel</button>
                  <button type="button" className="chats-btn" onClick={() => { closeSheet(); setPostLive(true); input.current?.focus({ preventScroll: true }); }}>Post to group</button>
                </div>
              </>,
            )}>Post to group</button>
          </div>
        )}
        {postLive && !recording && (
          <div className="chats-note-strip chats-note-strip--live">
            <span>{`Posting to everyone in ${current.name}`}</span>
            <button type="button" onClick={() => { setPostLive(false); input.current?.focus(); }}>Back to note</button>
          </div>
        )}
        {noteMode && !recording && (
          <div className="chats-note-strip">
            <span>Team note. {firstName} won't see this</span>
            <button type="button" onClick={() => { setNoteMode(false); input.current?.focus(); }}>Cancel</button>
          </div>
        )}
        {noting && !recording && live && !isTeam && (
          <MentionPicker chatId={current.id} text={text} onPick={(t) => { setText(t); input.current?.focus(); }} />
        )}
        <form className="chats-composer" onSubmit={sendText}>
          <input ref={photoPicker} type="file" accept="image/*" className="chats-file-picker" onChange={(e) => void pickFile(e)} tabIndex={-1} aria-hidden="true" />
          <input ref={pdfPicker} type="file" accept="application/pdf" className="chats-file-picker" onChange={(e) => void pickFile(e)} tabIndex={-1} aria-hidden="true" />
          {!recording && (
            <button type="button" className="chats-plus" aria-label={current.email ? 'Add: team note, address, suggest a reply' : 'Add: photo, document, team note, address, suggest a reply'} onClick={plusSheet}><PlusIcon /></button>
          )}
          {recording ? (
            <div className={`chats-rec${cancelling ? ' chats-rec--cancel' : ''}`}>
              <span className="chats-rec-dot" />
              <span>{clock}</span>
              <span className="chats-rec-hint">{cancelling ? 'Let go to cancel' : recordDy < -20 ? '↑ Keep going to lock' : '‹ Slide to cancel · ↑ Lock'}</span>
            </div>
          ) : (
            <div className={`chats-field${noting || othersChat ? ' chats-field--note' : ''}`}>
              <button type="button" className={`chats-icon-btn chats-icon-btn--field chats-emoji-btn${emojiOpen ? ' active' : ''}`} aria-pressed={emojiOpen} aria-label="Emojis" onClick={() => setEmojiOpen((o) => !o)}>
                <SmileIcon />
              </button>
              <textarea
                ref={input}
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={composerKey}
                placeholder={attach ? 'Add a caption' : leoNote ? 'What should Leo know or do in this chat?' : coachNote ? 'Note for head office and Ted (@Ted to ask him)' : noting ? 'Note for the team (@name alerts them)' : othersChat ? 'Type, then choose: note, Tell Leo or take over' : current.email ? 'Write an email reply' : current.aiState === 'ai' ? 'Type to take over from Leo' : 'Message'}
                aria-label={noting ? 'Team note' : 'Message'}
                autoComplete="off"
                enterKeyHint={isPhone && !wide ? 'enter' : 'send'}
              />
            </div>
          )}
          {/* The microphone while the box is empty, the send arrow once there is text. */}
          {/* Email has no voice notes: the send button stays, with an envelope (Gary, 19 Oct). */}
          {text.trim() || noting || attach || current.email || othersChat ? (
            // A press on send keeps the keyboard up, so the next message can follow straight on.
            <button type="submit" className={`chats-send${noting || othersChat ? ' chats-send--note' : current.email ? ' chats-send--email' : ''}`} aria-label={noting ? 'Add note' : current.email ? 'Send email' : 'Send'} disabled={sending || (!text.trim() && !attach)} onPointerDown={(e) => e.preventDefault()} onMouseDown={(e) => e.preventDefault()}>{current.email && !noting ? <MailIcon /> : <SendIcon />}</button>
          ) : (
            <button
              type="button"
              className={`chats-send${recording ? ' chats-send--recording' : ''}`}
              aria-label="Hold to record a voice note"
              onPointerDown={micDown}
              onPointerMove={micMove}
              onPointerUp={micUp}
              onPointerCancel={() => { if (!recording?.locked) cancelRecording(); }}
              onContextMenu={(e) => e.preventDefault()}
            >
              <MicIcon />
            </button>
          )}
        </form>
      </>
    );
  }

  return (
    <div className={`chats-conv${current.brand === 'pos' ? ' chats-brand-pos' : ''}`}>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack(isTeam ? '/team' : current.archived ? '/archived' : '/')}><BackIcon /></button>
        {/* Each panel's button on its own side (Gary, 18 Oct): the chat list's on the left. */}
        {wide && (
          <button type="button" className={`chats-icon-btn chats-panel-btn${panels.list ? '' : ' chats-panel-btn--off'}`} aria-pressed={!panels.list}
            aria-label={panels.list ? 'Hide the chat list' : 'Show the chat list'} title={panels.list ? 'Hide the chat list' : 'Show the chat list'}
            onClick={() => setPanel('list', !panels.list)}><PanelLeftIcon /></button>
        )}
        <button type="button" className="chats-conv-head" onClick={() => navigate(isTeam ? `/team/${current.id}/info` : `/chat/${current.id}/info`)}>
          {teamChat?.aiAvatar ? <TeamAvatar who={teamChat.aiAvatar} small /> : <Avatar name={current.name} colour={current.colour} small email={!!current.email} />}
          <span className="chats-conv-titles">
            {/* The customer's status beside the name (Gary, 18 Oct). */}
            {/* Beside the name on a computer; on a phone on the second line, so the name is not cut short (Gary, 18 Oct). */}
            <span className="chats-conv-name-line"><span className="chats-conv-name">{current.name}</span>{!isTeam && wide && <StatusChip chat={current} />}</span>
            {/* The card's tags, carried into the chat (Gary, 18 Oct); area and enquiry details are in Info. */}
            <span className="chats-conv-sub">{typing[current.id] ? 'typing…' : isTeam ? null : <ChatTags chat={current} headOffice={headOffice} labels={false} status={!wide} people={false} />}</span>
          </span>
        </button>
        {/* Labels get their own place on the right, so they never push who and stage about (Gary, 18 Oct).
            On a phone there is no room: they are in Info. */}
        {!isTeam && !!current.labels?.length && (
          <span className="chats-conv-labels" aria-label="Labels"><TagIcon /><LabelLine labels={current.labels} inline /></span>
        )}
        {room && (
          <button type="button" className={`chats-icon-btn chats-panel-btn${panels.info ? '' : ' chats-panel-btn--off'}`} aria-pressed={!panels.info}
            aria-label={panels.info ? 'Hide Info' : 'Show Info'} title={panels.info ? 'Hide Info' : 'Show Info'}
            onClick={() => setPanel('info', !panels.info)}><PanelRightIcon /></button>
        )}
        {!room && <button type="button" className="chats-conv-info" onClick={() => navigate(isTeam ? `/team/${current.id}/info` : `/chat/${current.id}/info`)}>Info</button>}
      </header>
      {pair && (
        <div className="chats-channel-tabs" role="tablist" aria-label="Conversation">
          {([['whatsapp', 'WhatsApp', pair.whatsapp], ['email', 'Email', pair.email]] as const).map(([key, label, c]) => (
            <button key={key} type="button" role="tab" aria-selected={c.id === current.id}
              className={`chats-channel-tab chats-channel-tab--${key}${c.id === current.id ? ' active' : ''}`}
              onClick={() => { if (c.id !== current.id) navigate(`/chat/${c.id}`, { replace: true }); }}>
              {key === 'email' ? <MailIcon /> : <BubbleIcon />}{label}
              {c.id !== current.id && c.unread > 0 && <span className="chats-badge chats-badge--sm chats-chip-count" aria-label={`${c.unread} unread`}>{c.unread}</span>}
            </button>
          ))}
        </div>
      )}
      {/* A Facebook or Instagram DM says so across the top, with Meta's reply window (Gary, 3 Nov 2026). */}
      {!isTeam && current.dm && (
        <div className={`chats-timer chats-timer--${current.dm}`}>
          <span className="chats-timer-text">{current.dm === 'facebook' ? 'Facebook Messenger' : 'Instagram message'}{current.dmPage ? ` to ${current.dmPage}` : ''}</span>
          {current.dmHumanOnly && <span className="chats-timer-text">Over 24 hours: a person may reply until 7 days</span>}
        </div>
      )}
      {/* An email chat says so in its own colour across the top (Gary, 19 Oct: it looked like WhatsApp). */}
      {!isTeam && current.email && (
        <div className="chats-timer chats-timer--email">
          {/* One line on a phone, the address cut with "..." (mobile review, 19 Oct: the bar took 40% of an iPhone SE). */}
          <span className="chats-timer-text"><MailIcon /><span className="chats-timer-email">{current.infoInbox ? 'Email to info@ from' : 'Email conversation with'} {current.email}</span></span>
          {/* System emails only (Gary, 19 Oct): nobody should expect their own mailbox to show here. On a phone it is
              in Info, under Emails (mobile review, 19 Oct). */}
          {wide && <span className="chats-timer-text">Only emails sent from Chats show here, not your own mailbox</span>}
          {/* Leo prepares, a person reviews and sends (Gary, 19 Oct: emails need no instant answer). Not on an older,
              closed email (mobile review, 19 Oct). */}
          {!oldEmail && lastCustomerMessage(current.thread)?.unanswered && (
            <button type="button" className="chats-timer-act" onClick={() => openSheet(<EmailDraftSheet chat={current} onUse={applyReplyLine} onClose={closeSheet} />)}>
              Leo's draft
            </button>
          )}
        </div>
      )}
      {!isTeam && <ChatStrip
        chat={current}
        onDrc={(action) => drcAction(current.id, action)}
        onLeoBackOn={canLeoOff ? leoBackOn : undefined}
        onOwner={headOffice && !isTeam ? () => openSheet(<ReassignSheet chat={current} />) : undefined}
      />}
      {!isTeam && askAvailable && onDuty && (
        <div className="chats-timer chats-timer--action">
          <span className="chats-timer-text">You're replying. Switch to Available?</span>
          <button type="button" className="chats-timer-act" onClick={() => void setLeoOnDuty(false).then((ok) => {
            setAskAvailable(false);
            toast(ok ? 'Available: you answer your chats within 10 minutes' : 'That did not save. Try again.');
          })}>Available</button>
          <button type="button" className="chats-timer-act" onClick={() => setAskAvailable(false)}>Not now</button>
        </div>
      )}
      {/* This week, pinned (Gary, 2 Nov 2026): the list is the focus, not the chat. */}
      {tedTasks.coaching && (() => {
        const today = new Date().toLocaleDateString('en-CA');
        const live = tedNames.filter((t) => t.status !== 'carried');
        const recorded = live.filter((t) => t.status === 'done' || t.status === 'not_done').length;
        const dueToday = live.filter((t) => t.status === 'open' && (dueOf(t, tedTasks.tasks) ?? today) <= today).length;
        return (
          <div className="chats-week-strip">
            <div className="chats-week-strip-main">
              <span className="chats-week-strip-text"><b>This week</b>{` ${recorded} of ${live.length} ticked off`}{dueToday > 0 ? ` · ${dueToday} due today` : ''}</span>
              <progress max={live.length || 1} value={recorded} aria-label={`${recorded} of ${live.length} ticked off this week`} />
            </div>
            <button type="button" className="chats-task-btn" aria-label={`Tasks, ${openTasks} open`} title="Ted's tasks for the week"
              onClick={() => { setInfoTab('tasks'); if (room) setPanel('info', true); else navigate(`/team/${current.id}/info`); }}>
              <TasksIcon />Tasks{openTasks > 0 && <span className="chats-badge chats-badge--sm">{openTasks}</span>}
            </button>
          </div>
        );
      })()}
      <Thread
        items={current.thread}
        earlier={current.earlier}
        focusId={focusId}
        typing={!!typing[current.id]}
        unreadAtOpen={unreadAtOpen}
        onReply={canReply ? startReply : undefined}
        onMenu={messageMenu}
        onLeadWhatsApp={!isTeam && !othersChat && current.phone && !current.windowOpen ? approvedSheet : undefined}
        onDone={isTeam && ((teamChat?.system && headOffice) || teamChat?.coaching) ? (m, done) => markTeamDone(m.id, done) : undefined}
        onReactions={isTeam ? whoReacted : undefined}
      />
      {bottom}
    </div>
  );
}
